import { useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import { useScale } from "common/utils";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import I18n from "features/shared/i18n";

interface ParentConfirmModalProps {
  open: boolean;
  title: string;
  message: string;
  icon?: string;
  variant?: "default" | "danger";
  confirmLabel?: string;
  cancelLabel?: string;
  onClose: () => void;
  onConfirm: () => void;
}

export const ParentConfirmModal = ({
  open,
  title,
  message,
  icon = "info",
  variant = "default",
  confirmLabel,
  cancelLabel,
  onClose,
  onConfirm,
}: ParentConfirmModalProps) => {
  const { width, scale } = useScale();
  // 狭い画面（scale下限クランプ時）でも収まるよう、設計幅を上限に可変にする
  const modalWidth = Math.min(600, width / scale - 16);
  const [disable, setDisable] = useState(false);

  useEffect(() => {
    if (open) {
      setDisable(false);
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    if (disable) return;
    setDisable(true);
    onConfirm();
  };

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div
            style={{ width: modalWidth }}
            className="flex-col-el border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30"
          >
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={handleClose} />
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">{icon}</span>
                <h2 className="text-[33px] text-textcolor-black whitespace-nowrap">{title}</h2>
              </div>
              <div className="flex-col-view items-start py-4">
                {message.split("\n").map((line, index) => (
                  <p key={index} className="text-body text-textcolor-black text-[24px] mb-2">
                    {line}
                  </p>
                ))}
              </div>

              <div className="flex-row-el flex-center gap-4 mt-4">
                <button
                  onClick={handleClose}
                  className="flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
                >
                  {cancelLabel ?? I18n.t("MSG_CANCEL_BTN_BY_PARENT")}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={disable}
                  className={`flex-row-el items-center justify-center px-6 py-3 border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ${
                    variant === "danger" ? "bg-danger text-white" : "bg-ink text-white"
                  }`}
                >
                  {confirmLabel ?? I18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
